import { type ColumnDef, DataTable } from "@monbits/ui";
import { ChevronRight, Clock, Wallet } from "lucide-react";
import * as React from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import type { PoolWithdrawal } from "src/providers/pool-withdrawal-provider";
import { usePendingPoolWithdrawals } from "src/queries/pool-withdrawal-queries";
import { statusConfig } from "./status-config";

const truncateAddress = (addr: string) =>
	`${addr.slice(0, 8)}...${addr.slice(-6)}`;

const columns: ColumnDef<PoolWithdrawal>[] = [
	{
		id: "id",
		header: "ID",
		cell: (pw) => <span className="font-medium">#{pw.id}</span>,
	},
	{
		id: "network",
		header: "Network",
		cell: (pw) => (
			<div className="flex items-center gap-2">
				<CryptoIcon ticker={pw.blockchain} className="h-5 w-5" />
				<span>{pw.blockchain}</span>
			</div>
		),
	},
	{
		id: "status",
		header: "Status",
		cell: (pw) => {
			const config = statusConfig[pw.status] ?? {
				label: pw.status,
				type: "default" as const,
				icon: Clock,
			};
			return (
				<StatusBadge type={config.type} icon={config.icon}>
					{config.label}
				</StatusBadge>
			);
		},
	},
	{
		id: "amount",
		header: "Amount",
		cell: (pw) => (
			<div className="flex items-center gap-2">
				<CryptoIcon ticker={pw.currency} className="h-4 w-4" />
				<span className="font-mono">{pw.amount}</span>
			</div>
		),
	},
	{
		id: "from",
		header: "From",
		cell: (pw) => (
			<span className="font-mono text-sm">
				{truncateAddress(pw.walletAddress)}
			</span>
		),
	},
	{
		id: "to",
		header: "To",
		cell: (pw) => (
			<span className="font-mono text-sm">
				{truncateAddress(pw.recipientAddress)}
			</span>
		),
	},
	{
		id: "created",
		header: "Created",
		cell: (pw) => <TimeLabel time={pw.createdAt} />,
	},
	{
		id: "arrow",
		header: "",
		className: "w-8",
		cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
	},
];

export const PendingWithdrawalsList: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const { data, isLoading } = usePendingPoolWithdrawals();
	const withdrawals = data?.withdrawals ?? [];

	const handleRowClick = React.useCallback(
		(pw: PoolWithdrawal) => {
			const queryString = searchParams.toString();
			const targetPath = queryString
				? `/pool-withdrawals/${pw.id}?${queryString}`
				: `/pool-withdrawals/${pw.id}`;
			navigate(targetPath);
		},
		[navigate, searchParams],
	);

	return (
		<>
			<DataTable
				columns={columns}
				data={withdrawals}
				isLoading={isLoading}
				onRowClick={handleRowClick}
				getRowId={(row) => String(row.id)}
				emptyMessage="No pending approvals"
				emptyDescription="Withdrawals requiring approval will appear here"
				emptyIcon={<Wallet className="h-8 w-8" />}
			/>
			<Outlet />
		</>
	);
};
